import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/primitives/button";
import { useNavigationState } from "@/zustand/NavigationState";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import { useRecordings } from "./useRecordings";
import Recording from "./Recording";
import RecordingsEmpty from "./RecordingsEmpty";

/** A few skeleton rows shown while the first page loads. */
function RecordingsLoading() {
  return (
    <div className="flex w-full flex-col gap-2">
      {Array.from({ length: 4 }, (_, index) => (
        <div
          key={index}
          className="flex w-full animate-pulse items-center gap-2"
        >
          <div className="size-10 shrink-0 rounded-sm bg-muted-foreground/5" />
          <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
            <div className="h-5 w-3/4 rounded-md bg-muted" />
            <div className="h-4 w-16 rounded-md bg-muted" />
          </div>
          <div className="mx-[6px] size-6 shrink-0 rounded-full bg-muted-foreground/5" />
        </div>
      ))}
    </div>
  );
}

/**
 * Recordings tab body — moderator-only list of a GROUP chat's recordings with
 * download + "load more" pagination. `ChatDetailsTabs` already gates the tab on
 * GROUP + moderator role, but this component re-derives the SAME gate from the
 * shared `activeChatRoleAsMember` source and passes it to `useRecordings` as
 * `enabled` — so the moderator-only request never fires even if this is ever
 * mounted outside the tab (defense-in-depth; the backend also 403s non-mods).
 */
export default function RecordingsList() {
  const { t } = useTranslation();
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );

  const isModerator =
    activeChatRoleAsMember === MemberRole.OWNER ||
    activeChatRoleAsMember === MemberRole.ADMIN ||
    activeChatRoleAsMember === MemberRole.MODERATOR;
  const canViewRecordings = activeChatType === ChatType.GROUP && isModerator;

  const {
    data,
    status,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useRecordings(activeChatId, canViewRecordings);

  if (!activeChatId || !canViewRecordings) {
    return null;
  }

  const recordings = data ? data.pages.flatMap((page) => page.recordings) : [];

  return (
    <div className="custom-scroll flex size-full flex-col overflow-y-auto p-1">
      {status === "pending" && <RecordingsLoading />}

      {status === "error" && (
        <div className="flex w-full flex-col items-center justify-center gap-3 p-4">
          <AlertCircle className="size-8 text-destructive" />
          <p className="text-center text-sm text-muted-foreground">
            {t("chatDetailsTabs.recordingsError")}
          </p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t("chatDetailsTabs.recordingsRetry")}
          </Button>
        </div>
      )}

      {status === "success" && recordings.length === 0 && <RecordingsEmpty />}

      {recordings.length > 0 && (
        <div className="flex w-full flex-col gap-1">
          {recordings.map((recording) => (
            <Recording key={recording.id} recording={recording} />
          ))}

          {hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2 self-center"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage
                ? t("chatDetailsTabs.recordingsLoading")
                : t("chatDetailsTabs.recordingsLoadMore")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
